import { useCallback, useEffect, useRef, useState } from 'react';

import { notification } from 'antd';

import { useOneButtonStore } from '../../store/oneButtonStore';

import quoteServices from '../../services/oneButtonServices/quoteServices';

import { buildSelectedQuoteFromOpportunityProductTypes } from '../../components/Layout-Components/Editor-Layout/oneBUTTONDashboard/buildGenerateQuoteFromFinanceLine';
import { evaluateGenerateQuoteSyncGate } from '../../components/Layout-Components/Editor-Layout/oneBUTTONDashboard/generateQuoteSyncGate';
import { getSyncedLeadIdForPartner } from '../../components/Layout-Components/Editor-Layout/oneBUTTONDashboard/financePartnerChecklistHelpers';
import {
    financePartnerProductTypeLabel,
    isFinancePartnerSyncFlagDone,
    loadFinancePartnerSyncStatuses,
} from '../../components/Layout-Components/Editor-Layout/oneBUTTONDashboard/utils/finance';
import { resolvePciPartnerChecklistBlockedReason } from '../lib/preContractPartnerSyncGate';

import type { selectedQuote } from '../../components/Layout-Components/Editor-Layout/oneBUTTONDashboard/FinancingGridCard';

import type { PreContractProduct } from '../lib/preContractTypes';

import {

    partnerKeysEqual,
    resolvePartnerSyncState,

    type PreContractDesignPartnerRecommendation,

} from '../lib/preContractProductPartners';

import {

    buildInitialGenerateSteps,
    canRunGenerateStep,

    partnerSkipsQuotePreSync,

    runPreContractProductGeneratePipeline,
    runPreContractProductGenerateStep,

    buildOpportunityGenerateSteps,
    QUOTE_FORM_DISMISSED,
    isGenerateStepDismissed,
    formatLinkedQuoteDetail,
    latestQuoteIdsFromGetQuotes,
    type ProductGenerateStep,
    type ProductGenerateStepId,
} from '../lib/preContractProductGeneratePipeline';

import { hydrateOpportunityStore, resolveStoreOpportunityId } from '../lib/preContractTitle';
import type { PreContractQuoteReview } from '../lib/preContractQuoteReview';



type QuoteWaiter = {
    resolve: (quoteId?: string) => void;
    reject: (error?: Error) => void;
};

type PartnerInputWaiter = {
    resolve: () => void;
    reject: (error?: Error) => void;
};

export type PreContractPartnerSyncResult = {

    ok: boolean;

    message?: string;

};



export type UsePreContractProductGenerateArgs = {

    salesForceId: string;

    generateDisabled?: boolean;

    generateDisabledReason?: string;

    isGenerateQuotePermissionDisabled?: boolean;

    productPartners: Partial<

        Record<PreContractProduct, { partner: string | null; method: string | null }>

    >;

    resolveDesignPartner?: (

        product: PreContractProduct,

    ) => PreContractDesignPartnerRecommendation | null;

    syncDesignPartner?: (

        product: PreContractProduct,

        recommendation: PreContractDesignPartnerRecommendation,

    ) => Promise<PreContractPartnerSyncResult>;

    onOpenGenerateQuote: (payload: selectedQuote, product: PreContractProduct) => void;

    onGenerateComplete?: () => void;

    resolveQuoteReview: (
        product: PreContractProduct,
        partnerName: string,
        financeMethod: string,
    ) => PreContractQuoteReview;
    needsFinalizeDesign?: (product: PreContractProduct) => boolean;
    onFinalizeDesign?: (product: PreContractProduct) => Promise<void>;
    needsUtilityDetails?: (product: PreContractProduct) => boolean;
    onSaveUtilityDetails?: (product: PreContractProduct) => Promise<void>;
    onOpenPartnerSetup?: (product: PreContractProduct) => void | Promise<void>;
};



function patchStep(

    steps: ProductGenerateStep[],

    id: ProductGenerateStepId,

    patch: Partial<ProductGenerateStep>,

): ProductGenerateStep[] {

    return steps.map((step) => (step.id === id ? { ...step, ...patch } : step));

}



function readOpportunityProductPartner(

    product: PreContractProduct,

): { partner: string | null; method: string | null } {

    const oppProducts = useOneButtonStore.getState().opportunityDetails?.OpportunityProduct ?? [];

    const row = oppProducts.find(

        (entry: { Product_Type_Name?: string; ActiveFlag?: boolean }) =>

            entry.Product_Type_Name === product && entry.ActiveFlag !== false,

    );

    return {

        partner: row?.Finance_Partner_Display_Name ?? null,

        method: row?.Finance_Method_Name ?? null,

    };

}



export function usePreContractProductGenerate({

    salesForceId,

    generateDisabled = false,

    generateDisabledReason,

    isGenerateQuotePermissionDisabled = false,

    productPartners,

    resolveDesignPartner,

    syncDesignPartner,

    onOpenGenerateQuote,

    onGenerateComplete,

    resolveQuoteReview,
    needsFinalizeDesign,
    onFinalizeDesign,
    needsUtilityDetails,
    onSaveUtilityDetails,
    onOpenPartnerSetup,

}: UsePreContractProductGenerateArgs) {


    const [activeProduct, setActiveProduct] = useState<PreContractProduct | null>(null);

    const [steps, setSteps] = useState<ProductGenerateStep[]>([]);

    const [running, setRunning] = useState(false);

    const [review, setReview] = useState<PreContractQuoteReview | null>(null);
    const [linkedQuotes, setLinkedQuotes] = useState<Partial<Record<PreContractProduct, string>>>({});
    const savedPartnersRef = useRef(productPartners);

    const quoteWaiterRef = useRef<QuoteWaiter | null>(null);
    const quoteCompletedRef = useRef(false);
    const unlinkedQuoteProductsRef = useRef<Set<PreContractProduct>>(new Set());

    const partnerInputWaiterRef = useRef<PartnerInputWaiter | null>(null);

    const partnerInputWasOpenRef = useRef(false);

    const pipelineProductRef = useRef<PreContractProduct | null>(null);

    const leadSyncFormOpen = useOneButtonStore((state: any) => state.leadSyncFormOpen);

    const igsLeadSyncModalOpen = useOneButtonStore((state: any) => state.igsLeadSyncModalOpen);

    const isBackgroundSyncing = useOneButtonStore((state: any) => state.isBackgroundSyncing);

    useEffect(() => {
        const inputOpen = leadSyncFormOpen || igsLeadSyncModalOpen;
        if (inputOpen) {
            partnerInputWasOpenRef.current = true;
            return;
        }
        if (
            isBackgroundSyncing ||
            !partnerInputWasOpenRef.current ||
            !partnerInputWaiterRef.current
        ) {
            return;
        }
        const timer = window.setTimeout(() => {
            const state = useOneButtonStore.getState();
            if (
                state.leadSyncFormOpen ||
                state.igsLeadSyncModalOpen ||
                state.isBackgroundSyncing ||
                !partnerInputWaiterRef.current
            ) {
                return;
            }
            partnerInputWasOpenRef.current = false;
            partnerInputWaiterRef.current.resolve();
            partnerInputWaiterRef.current = null;
        }, 200);
        return () => window.clearTimeout(timer);
    }, [igsLeadSyncModalOpen, isBackgroundSyncing, leadSyncFormOpen]);

    useEffect(
        () => () => {
            partnerInputWaiterRef.current?.reject(
                new Error('Partner sync was cancelled.'),
            );
            partnerInputWaiterRef.current = null;
        },
        [],
    );



    const cancelQuoteWait = useCallback((message = 'Quote was cancelled.') => {
        if (quoteCompletedRef.current) return;
        quoteWaiterRef.current?.reject(new Error(message));
        quoteWaiterRef.current = null;
        pipelineProductRef.current = null;
    }, []);

    const dismissQuoteWait = useCallback(() => {
        if (quoteCompletedRef.current) return;
        quoteWaiterRef.current?.reject(new Error(QUOTE_FORM_DISMISSED));
        quoteWaiterRef.current = null;
    }, []);

    const notifyQuoteSaved = useCallback((quoteId?: string) => {
        quoteCompletedRef.current = true;
        const product = pipelineProductRef.current;
        const id = String(quoteId ?? '').trim();
        if (product && id) {
            unlinkedQuoteProductsRef.current.delete(product);
            setLinkedQuotes((prev) => ({ ...prev, [product]: id }));
            setSteps((prev) =>
                prev.map((step) =>
                    step.id === 'quote'
                        ? {
                              ...step,
                              status: 'done',
                              quoteId: id,
                              detail: formatLinkedQuoteDetail(id),
                          }
                        : step,
                ),
            );
        }
        quoteWaiterRef.current?.resolve(id || undefined);
        quoteWaiterRef.current = null;
    }, []);

    useEffect(() => {
        const previous = savedPartnersRef.current;
        savedPartnersRef.current = productPartners;
        (Object.keys(productPartners) as PreContractProduct[]).forEach((product) => {
            const nextPartner = productPartners[product]?.partner ?? null;
            const previousPartner = previous[product]?.partner ?? null;
            if (
                previousPartner &&
                nextPartner &&
                !partnerKeysEqual(previousPartner, nextPartner)
            ) {
                setLinkedQuotes((prev) => {
                    if (!prev[product]) return prev;
                    const next = { ...prev };
                    delete next[product];
                    return next;
                });
            }
        });
    }, [productPartners]);

    const salesForceIdForQuotes = useOneButtonStore((state: any) => state.salesForceId);
    const quotesRefreshTrigger = useOneButtonStore((state: any) => state.quotesRefreshTrigger);

    useEffect(() => {
        const sfid = String(salesForceIdForQuotes || salesForceId || '').trim();
        if (!sfid || sfid === '—') return;
        let cancelled = false;
        void quoteServices
            .getQuotes(sfid)
            .then((raw) => {
                if (cancelled) return;
                const mapped = latestQuoteIdsFromGetQuotes(raw, productPartners);
                setLinkedQuotes((prev) => {
                    const next = { ...prev };
                    (Object.keys(productPartners) as PreContractProduct[]).forEach((product) => {
                        if (unlinkedQuoteProductsRef.current.has(product)) {
                            delete next[product];
                            return;
                        }
                        const id = mapped[product];
                        if (id) next[product] = id;
                        else delete next[product];
                    });
                    return next;
                });
            })
            .catch(() => {
                /* preview still works without a prior quote id */
            });
        return () => {
            cancelled = true;
        };
    }, [productPartners, quotesRefreshTrigger, salesForceId, salesForceIdForQuotes]);

    const unlinkQuote = useCallback((product: PreContractProduct) => {
        unlinkedQuoteProductsRef.current.add(product);
        setLinkedQuotes((prev) => {
            const next = { ...prev };
            delete next[product];
            return next;
        });
        setSteps((prev) =>
            prev.map((step) =>
                step.id === 'quote'
                    ? {
                          ...step,
                          status: 'pending' as const,
                          quoteId: undefined,
                          detail: 'Click to run',
                      }
                    : step,
            ),
        );
    }, []);

    const updateStep = useCallback((id: ProductGenerateStepId, patch: Partial<ProductGenerateStep>) => {

        setSteps((prev) => patchStep(prev, id, patch));

    }, []);

    const waitForRequiredLeadInput = useCallback(
        (product: PreContractProduct, partnerName: string) =>
            new Promise<void>((resolve, reject) => {
                partnerInputWaiterRef.current = { resolve, reject };
                partnerInputWasOpenRef.current = false;
                const store = useOneButtonStore.getState();
                store.setFinancePartnerSyncStatus(
                    partnerName,
                    { leadSyncAction: 'sync_existing' },
                    product,
                );
                if (partnerName.trim().toLowerCase() === 'igs') {
                    store.setIgsLeadSyncModalOpen(true, product);
                } else {
                    store.setLeadSyncFormOpen(true, 'sync_existing', partnerName, {
                        productName: product,
                    });
                }
            }),
        [],
    );

    const refreshLeadStepFromSyncStatus = useCallback(
        async (product: PreContractProduct, salesForceIdValue: string) => {
            const oppId =
                resolveStoreOpportunityId() ?? (await hydrateOpportunityStore(salesForceIdValue));
            let syncList = useOneButtonStore.getState().financePartnerRawSyncStatuses ?? [];
            if (oppId) {
                try {
                    syncList = await loadFinancePartnerSyncStatuses(String(oppId));
                    useOneButtonStore.getState().setFinancePartnerRawSyncStatuses(syncList);
                } catch {
                    /* keep last known statuses */
                }
            }
            const productType = financePartnerProductTypeLabel(product);
            const leadReady = isFinancePartnerSyncFlagDone(syncList, productType, 'lead');
            const contactReady = isFinancePartnerSyncFlagDone(syncList, productType, 'contact');
            return { leadReady, contactReady };
        },
        [],
    );



    const runProductGenerate = useCallback(

        async (product: PreContractProduct) => {

            const designPartner = resolveDesignPartner?.(product) ?? null;

            const fallbackPartner = productPartners[product]?.partner?.trim() ?? '';

            let partnerName = designPartner?.partner?.trim() || fallbackPartner;



            if (!partnerName) {

                throw new Error('Finance partner is required before generating.');

            }



            const savedPartner = readOpportunityProductPartner(product);

            const partnerSyncState = designPartner

                ? resolvePartnerSyncState(savedPartner, designPartner)

                : 'match';

            const needsPartnerSync =

                Boolean(designPartner?.available && designPartner.partner.trim()) &&

                partnerSyncState !== 'match' &&

                Boolean(syncDesignPartner);

            const needsFinalize = Boolean(needsFinalizeDesign?.(product));
            const needsUtility = Boolean(needsUtilityDetails?.(product));

            if (needsFinalize) {
                notification.info({
                    message: `${product} finalization pending`,
                    description:
                        'Finalize the selected oneDRAW design, then update the product / finance partner before later steps.',
                    placement: 'topRight',
                });
                return;
            }
            if (needsUtility) {
                notification.info({
                    message: `${product} utility details changed`,
                    description: 'Save utility details before generating.',
                    placement: 'topRight',
                });
                return;
            }

            let nextSteps = buildOpportunityGenerateSteps(

                product,

                partnerName,

                { needsPartnerSync, needsFinalize, needsUtilityDetails: needsUtility },

            );



            setRunning(true);

            setActiveProduct(product);

            setSteps(nextSteps);

            pipelineProductRef.current = product;



            try {

                const oppId =

                    resolveStoreOpportunityId() ?? (await hydrateOpportunityStore(salesForceId));

                if (!oppId) {

                    throw new Error('Opportunity is not loaded.');

                }



                if (needsPartnerSync && designPartner && syncDesignPartner) {

                    updateStep('partnerSync', { status: 'running', detail: 'Saving design partner…' });

                    const syncResult = await syncDesignPartner(product, designPartner);

                    if (!syncResult.ok) {

                        const message = syncResult.message ?? 'Partner sync failed.';

                        updateStep('partnerSync', { status: 'error', detail: message });

                        notification.error({

                            message: `${product} partner sync failed`,

                            description: message,

                            placement: 'topRight',

                        });

                        return;

                    }



                    await hydrateOpportunityStore(salesForceId);

                    partnerName = designPartner.partner.trim() || partnerName;

                    nextSteps = [

                        {

                            id: 'partnerSync',

                            label: 'Sync partner',

                            status: 'done',

                            detail: `${partnerName} saved`,

                        },

                        ...buildInitialGenerateSteps(product, partnerName),

                    ];

                    setSteps(nextSteps);

                } else if (designPartner?.partner?.trim()) {

                    partnerName = designPartner.partner.trim();

                    nextSteps = buildInitialGenerateSteps(product, partnerName);

                    setSteps(nextSteps);

                }



                let opportunity = useOneButtonStore.getState().opportunityDetails;

                if (!opportunity) {

                    await hydrateOpportunityStore(salesForceId);

                    opportunity = useOneButtonStore.getState().opportunityDetails;

                }

                if (!opportunity) {

                    throw new Error('Opportunity details are not available.');

                }

                const storeState = useOneButtonStore.getState();
                const syncGate = evaluateGenerateQuoteSyncGate({
                    opportunityDetails: opportunity,
                    financePartnerSyncStatus: storeState.financePartnerSyncStatus,
                    financePartnerRawSyncStatuses: storeState.financePartnerRawSyncStatuses,
                    scope: {
                        quotePartner: partnerName,
                        quoteProductTypes: [product as 'Solar' | 'Roof' | 'Battery'],
                    },
                });
                let syncedLeadId = getSyncedLeadIdForPartner(
                    opportunity,
                    partnerName,
                    product,
                );
                if (
                    !partnerSkipsQuotePreSync(partnerName) &&
                    syncGate.blocked &&
                    !syncedLeadId
                ) {
                    updateStep('lead', {
                        status: 'running',
                        detail: 'Complete lead sync to continue…',
                    });
                    await waitForRequiredLeadInput(product, partnerName);
                    await hydrateOpportunityStore(salesForceId);
                    opportunity = useOneButtonStore.getState().opportunityDetails;
                    syncedLeadId = getSyncedLeadIdForPartner(
                        opportunity,
                        partnerName,
                        product,
                    );
                    if (!syncedLeadId) {
                        updateStep('lead', {
                            status: 'error',
                            detail: 'Lead sync was not completed',
                        });
                        notification.info({
                            message: `${product} generation paused`,
                            description: 'Complete lead sync, then run the quote again.',
                            placement: 'topRight',
                        });
                        return;
                    }
                }

                const selectedQuotePayload = buildSelectedQuoteFromOpportunityProductTypes(

                    [product as 'Solar' | 'Roof' | 'Battery'],

                    opportunity,

                );

                if (!selectedQuotePayload) {

                    throw new Error(

                        'Active opportunity product with finance partner is required before generating.',

                    );

                }



                let priceQuote = useOneButtonStore.getState().priceQuoteDetails;

                if (!priceQuote) {

                    const fetched = await quoteServices.getRetrievePriceQuote(String(oppId));

                    if (fetched) {
                        useOneButtonStore.getState().setPriceQuoteDetails(
                            fetched as unknown as NonNullable<
                                ReturnType<typeof useOneButtonStore.getState>['priceQuoteDetails']
                            >,
                        );
                        priceQuote = useOneButtonStore.getState().priceQuoteDetails;
                    }

                }

                if (!priceQuote) {

                    throw new Error('Could not load price quote details for this opportunity.');

                }



                const result = await runPreContractProductGeneratePipeline({

                    product,

                    opportunityId: String(oppId),

                    partnerName,

                    leadAlreadyConfirmed: Boolean(syncedLeadId),

                    onStepUpdate: (step) => {

                        setSteps((prev) => patchStep(prev, step.id, step));

                    },

                    waitForQuoteSaved: () =>
                        new Promise<string | undefined>((resolve, reject) => {
                            quoteCompletedRef.current = false;
                            quoteWaiterRef.current = { resolve, reject };
                            onOpenGenerateQuote(selectedQuotePayload, product);
                        }),

                });



                if (result.dismissed) {
                    return;
                }

                if (result.ok) {

                    notification.success({

                        message: `${product} generated`,

                        description: 'Quote and contract steps completed successfully.',

                        placement: 'topRight',

                    });

                    onGenerateComplete?.();

                } else if (result.error) {

                    notification.error({

                        message: `${product} generation stopped`,

                        description: result.error,

                        placement: 'topRight',

                    });

                }

            } catch (error) {

                const message = error instanceof Error ? error.message : 'Generation failed.';

                notification.error({

                    message: `${product} generation failed`,

                    description: message,

                    placement: 'topRight',

                });

            } finally {

                setRunning(false);

                pipelineProductRef.current = null;

                quoteWaiterRef.current = null;

            }

        },

        [

            onGenerateComplete,

            onOpenGenerateQuote,

            productPartners,

            resolveDesignPartner,

            needsFinalizeDesign,

            needsUtilityDetails,

            salesForceId,

            syncDesignPartner,

            needsFinalizeDesign,

            updateStep,

            waitForRequiredLeadInput,

        ],

    );



    const runGenerateStep = useCallback(
        async (product: PreContractProduct, stepId: ProductGenerateStepId) => {
            if (running) return;
            const needsFinalize = Boolean(needsFinalizeDesign?.(product));
            const needsUtility = Boolean(needsUtilityDetails?.(product));
            const isSetupStep =
                stepId === 'finalize' ||
                stepId === 'utilityDetails' ||
                stepId === 'partnerSync';
            if (!isSetupStep && (generateDisabled || isGenerateQuotePermissionDisabled)) {
                return;
            }

            const designPartner = resolveDesignPartner?.(product);
            let partnerName =
                designPartner?.partner?.trim() ?? productPartners[product]?.partner?.trim();
            if (!isSetupStep && !partnerName) {
                notification.warning({
                    message: `${product} partner required`,
                    description: 'Select a finance partner on the product card before running a step.',
                    placement: 'topRight',
                });
                return;
            }
            if (!isSetupStep && designPartner && !designPartner.available) {
                notification.warning({
                    message: `${product} partner unavailable`,
                    description:
                        designPartner.reason ??
                        'Choose an available finance option before running a step.',
                    placement: 'topRight',
                });
                return;
            }

            const savedPartner = readOpportunityProductPartner(product);
            const partnerSyncState = designPartner
                ? resolvePartnerSyncState(savedPartner, designPartner)
                : 'match';
            const needsPartnerSync =
                Boolean(designPartner?.available && designPartner.partner.trim()) &&
                partnerSyncState !== 'match' &&
                Boolean(syncDesignPartner);

            const nextSteps = buildOpportunityGenerateSteps(product, partnerName ?? '', {
                needsFinalize,
                needsUtilityDetails: needsUtility,
                needsPartnerSync,
            }).map((step) => steps.find((entry) => entry.id === step.id) ?? step);
            if (!canRunGenerateStep(nextSteps, stepId)) {
                notification.info({
                    message: `${product} step locked`,
                    description: 'Complete the previous required step first.',
                    placement: 'topRight',
                });
                return;
            }

            setActiveProduct(product);
            setSteps(nextSteps);
            setRunning(true);
            pipelineProductRef.current = product;
            const priorStep = nextSteps.find((step) => step.id === stepId);
            const restorePriorStep = () => {
                if (!priorStep) return;
                setSteps((prev) =>
                    prev.map((step) => (step.id === stepId ? { ...priorStep } : step)),
                );
            };

            try {
                if (stepId === 'finalize') {
                    if (!onFinalizeDesign) {
                        updateStep('finalize', {
                            status: 'error',
                            detail: 'Finalize is not available.',
                        });
                        return;
                    }
                    updateStep('finalize', {
                        status: 'running',
                        detail: 'Opening the selected design to confirm…',
                    });
                    try {
                        await onFinalizeDesign(product);
                    } catch (error) {
                        if (isGenerateStepDismissed(error)) {
                            restorePriorStep();
                            return;
                        }
                        throw error;
                    }
                    const stillNeeded = Boolean(needsFinalizeDesign?.(product));
                    updateStep('finalize', {
                        status: stillNeeded ? 'pending' : 'done',
                        detail: stillNeeded
                            ? 'Confirm and save the selected design'
                            : 'Design saved',
                    });
                    if (!stillNeeded) {
                        setSteps(
                            buildOpportunityGenerateSteps(product, partnerName ?? '', {
                                needsFinalize: false,
                                needsUtilityDetails: needsUtility,
                                needsPartnerSync,
                            }),
                        );
                    }
                    return;
                }

                if (stepId === 'utilityDetails') {
                    if (!onSaveUtilityDetails) {
                        updateStep('utilityDetails', {
                            status: 'error',
                            detail: 'Utility save is not available.',
                        });
                        return;
                    }
                    updateStep('utilityDetails', {
                        status: 'running',
                        detail: 'Saving utility details…',
                    });
                    await onSaveUtilityDetails(product);
                    updateStep('utilityDetails', {
                        status: 'done',
                        detail: 'Utility details saved',
                    });
                    return;
                }

                const oppId =
                    resolveStoreOpportunityId() ?? (await hydrateOpportunityStore(salesForceId));
                if (!oppId) throw new Error('Opportunity is not loaded.');

                if (stepId === 'partnerSync') {
                    if (needsFinalize) {
                        notification.info({
                            message: `${product} step locked`,
                            description: 'Finalize the oneDRAW design first.',
                            placement: 'topRight',
                        });
                        return;
                    }
                    if (needsUtility) {
                        notification.info({
                            message: `${product} step locked`,
                            description: 'Update utility details first.',
                            placement: 'topRight',
                        });
                        return;
                    }
                    if (onOpenPartnerSetup) {
                        updateStep('partnerSync', {
                            status: 'running',
                            detail: 'Open products setup to choose a finance partner…',
                        });
                        await onOpenPartnerSetup(product);
                        const stillNeeded = Boolean(
                            !readOpportunityProductPartner(product).partner?.trim() ||
                                (designPartner &&
                                    resolvePartnerSyncState(
                                        readOpportunityProductPartner(product),
                                        designPartner,
                                    ) !== 'match'),
                        );
                        updateStep('partnerSync', {
                            status: stillNeeded ? 'pending' : 'done',
                            detail: stillNeeded
                                ? 'Choose and save the finance partner'
                                : `${readOpportunityProductPartner(product).partner?.trim() || partnerName} saved`,
                        });
                        return;
                    }
                    if (!needsPartnerSync || !designPartner || !syncDesignPartner) {
                        updateStep('partnerSync', {
                            status: 'synced',
                            detail: 'Partner already saved',
                        });
                        return;
                    }
                    updateStep('partnerSync', {
                        status: 'running',
                        detail: 'Saving design partner…',
                    });
                    const syncResult = await syncDesignPartner(product, designPartner);
                    if (!syncResult.ok) {
                        const message = syncResult.message ?? 'Partner sync failed.';
                        updateStep('partnerSync', { status: 'error', detail: message });
                        notification.error({
                            message: `${product} partner sync failed`,
                            description: message,
                            placement: 'topRight',
                        });
                        return;
                    }
                    await hydrateOpportunityStore(salesForceId);
                    updateStep('partnerSync', {
                        status: 'done',
                        detail: `${designPartner.partner.trim()} saved`,
                    });
                    return;
                }

                const partnerSyncBlock = resolvePciPartnerChecklistBlockedReason(
                    product,
                    partnerName ?? '',
                    stepId,
                );
                if (partnerSyncBlock) {
                    updateStep(stepId, {
                        status: 'pending',
                        detail: partnerSyncBlock,
                        blockedReason: partnerSyncBlock,
                    });
                    notification.warning({
                        message: 'Design required for Partner Sync',
                        description: partnerSyncBlock,
                        placement: 'topRight',
                    });
                    return;
                }

                if (stepId === 'lead') {
                    updateStep('lead', {
                        status: 'running',
                        detail: 'Open lead sync…',
                    });
                    try {
                        await waitForRequiredLeadInput(product, partnerName ?? '');
                    } catch (error) {
                        if (isGenerateStepDismissed(error)) {
                            restorePriorStep();
                            return;
                        }
                        throw error;
                    }
                    const { leadReady, contactReady } = await refreshLeadStepFromSyncStatus(
                        product,
                        salesForceId,
                    );
                    if (leadReady) {
                        updateStep('lead', {
                            status: 'synced',
                            detail: contactReady
                                ? 'Lead and contact already synced'
                                : 'Lead ready',
                        });
                    } else {
                        restorePriorStep();
                    }
                    return;
                }

                let opportunity = useOneButtonStore.getState().opportunityDetails;
                if (!opportunity) {
                    await hydrateOpportunityStore(salesForceId);
                    opportunity = useOneButtonStore.getState().opportunityDetails;
                }
                if (!opportunity) throw new Error('Opportunity details are not available.');

                const selectedQuotePayload = buildSelectedQuoteFromOpportunityProductTypes(
                    [product as 'Solar' | 'Roof' | 'Battery'],
                    opportunity,
                );
                if (!selectedQuotePayload && stepId === 'quote') {
                    throw new Error(
                        'Active opportunity product with finance partner is required before creating a quote.',
                    );
                }

                const result = await runPreContractProductGenerateStep({
                    stepId,
                    product,
                    opportunityId: String(oppId),
                    partnerName: partnerName ?? '',
                    leadAlreadyConfirmed: Boolean(
                        getSyncedLeadIdForPartner(opportunity, partnerName ?? '', product),
                    ),
                    onStepUpdate: (step) => {
                        setSteps((prev) => patchStep(prev, step.id, step));
                    },
                    waitForQuoteSaved: () =>
                        new Promise<string | undefined>((resolve, reject) => {
                            quoteCompletedRef.current = false;
                            quoteWaiterRef.current = { resolve, reject };
                            if (selectedQuotePayload) {
                                onOpenGenerateQuote(selectedQuotePayload, product);
                            } else {
                                reject(new Error('Could not open the quote form.'));
                            }
                        }),
                });

                if (result.dismissed) {
                    restorePriorStep();
                    return;
                }
                if (result.ok && stepId === 'contract') {
                    onGenerateComplete?.();
                } else if (result.error) {
                    notification.error({
                        message: `${product} step failed`,
                        description: result.error,
                        placement: 'topRight',
                    });
                }
            } catch (error) {
                if (isGenerateStepDismissed(error)) {
                    restorePriorStep();
                    return;
                }
                const message = error instanceof Error ? error.message : 'Step failed.';
                updateStep(stepId, { status: 'error', detail: message });
                notification.error({
                    message: `${product} step failed`,
                    description: message,
                    placement: 'topRight',
                });
            } finally {
                setRunning(false);
                pipelineProductRef.current = null;
                quoteWaiterRef.current = null;
            }
        },
        [
            generateDisabled,
            isGenerateQuotePermissionDisabled,
            needsFinalizeDesign,
            needsUtilityDetails,
            onFinalizeDesign,
            onSaveUtilityDetails,
            onOpenPartnerSetup,
            onGenerateComplete,
            onOpenGenerateQuote,
            productPartners,
            refreshLeadStepFromSyncStatus,
            resolveDesignPartner,
            running,
            salesForceId,
            steps,
            syncDesignPartner,
            updateStep,
            waitForRequiredLeadInput,
        ],
    );

    const startGenerate = useCallback(

        (product: PreContractProduct) => {

            if (running || generateDisabled || isGenerateQuotePermissionDisabled) return;

            if (needsFinalizeDesign?.(product)) {
                notification.info({
                    message: `${product} finalization pending`,
                    description:
                        'Finalize the selected oneDRAW design first, then update the product / finance partner.',
                    placement: 'topRight',
                });
                return;
            }
            if (needsUtilityDetails?.(product)) {
                notification.info({
                    message: `${product} utility details changed`,
                    description: 'Save utility details before creating a quote.',
                    placement: 'topRight',
                });
                return;
            }



            const designPartner = resolveDesignPartner?.(product);

            const partnerName =

                designPartner?.partner?.trim() ?? productPartners[product]?.partner?.trim();

            if (!partnerName) {

                notification.warning({

                    message: `${product} partner required`,

                    description:

                        'Select a finance partner on the product card before generating.',

                    placement: 'topRight',

                });

                return;

            }



            if (designPartner && !designPartner.available) {

                notification.warning({

                    message: `${product} partner unavailable`,

                    description:

                        designPartner.reason ??

                        'Choose an available finance option before generating.',

                    placement: 'topRight',

                });

                return;

            }



            const financeMethod =
                designPartner?.financeMethod?.trim() ??
                productPartners[product]?.method?.trim() ??
                '';
            setReview(resolveQuoteReview(product, partnerName, financeMethod));

        },

        [

            generateDisabled,

            isGenerateQuotePermissionDisabled,

            needsFinalizeDesign,

            needsUtilityDetails,

            productPartners,

            resolveDesignPartner,

            resolveQuoteReview,

            running,

        ],

    );

    const confirmReview = useCallback(() => {
        const product = review?.product;
        if (!product || running) return;
        setReview(null);
        void runProductGenerate(product);
    }, [review, runProductGenerate, running]);

    const cancelReview = useCallback(() => {
        if (!running) setReview(null);
    }, [running]);



    return {

        activeProduct,

        steps,

        running,

        review,

        startGenerate,
        runGenerateStep,

        confirmReview,

        cancelReview,

        notifyQuoteSaved,

        cancelQuoteWait,
        dismissQuoteWait,
        linkedQuotes,
        unlinkQuote,

        pipelineProductRef,

    };

}


